/**
 * Studio timezone — Advanced tab (WSRADM-0047 / WSRAPI-0152).
 *
 * Decides which day a sale, a check-in and a report bucket belong to. Self-loading;
 * mounted by `AdvancedPanel` only for roles that may save (the server decides, this
 * only avoids showing a control the role cannot use — PERM-001). Explicit Save with
 * a consequence confirmation; `changed:false` means no rebuild. There is no clear /
 * reset: the setting can only be changed (`source: 'DEFAULT'` = running on UTC).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  getTimezone,
  isInvalidTimezone,
  putTimezone,
  type TimezoneSetting,
} from '@/api/configuration/timezone';
import { ApiError } from '@/api/errors';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useResource } from '@/hooks/useResource';
import { detailsCode } from './detailsCode';
import { browserZone, supportedZones, zoneLabel, zoneOptions } from './timezoneModel';

export interface TimezoneSettingsProps {
  /** Report the load's `UNKNOWN_TENANT` so the page shows its unknown-tenant state. */
  onUnknownTenant: (unknown: boolean) => void;
}

export function TimezoneSettings({ onUnknownTenant }: TimezoneSettingsProps) {
  const { t } = useCopy();
  const resource = useResource(() => getTimezone(), []);
  // The authoritative zone after a save (the response, not the picker).
  const [saved, setSaved] = useState<TimezoneSetting | null>(null);
  // `null` until the person touches the picker; then their choice.
  const [picked, setPicked] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refocus, setRefocus] = useState(0);
  const saveRef = useRef<HTMLButtonElement | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);

  const unknownTenant = detailsCode(resource.error) === 'UNKNOWN_TENANT';
  useEffect(() => {
    onUnknownTenant(unknownTenant);
    return () => onUnknownTenant(false);
  }, [unknownTenant, onUnknownTenant]);

  // After a save settles, put focus back on Save — or on the picker when Save is
  // now disabled (nothing left to save) so keyboard focus is never dropped (A11Y-010).
  useEffect(() => {
    if (refocus === 0) return;
    const save = saveRef.current;
    (save && !save.disabled ? save : pickerRef.current)?.focus();
  }, [refocus]);

  const current = saved ?? resource.data;
  const browser = useMemo(() => browserZone(), []);
  const zones = useMemo(() => supportedZones(), []);
  const options = useMemo(
    () => (zones === null ? null : zoneOptions(zones, [current?.timezone ?? null, browser])),
    [zones, current?.timezone, browser],
  );

  if (unknownTenant) return null;

  if (resource.status === 'error') {
    const headline = t('config.timezone.loadError', 'Could not load the studio timezone.');
    return (
      <Alert
        severity="error"
        sx={{ mt: 4 }}
        action={
          <Button color="inherit" size="small" onClick={resource.refetch}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {headline}
        <ErrorDetails error={resource.error} headline={headline} intent="read" />
      </Alert>
    );
  }

  if (current === null) {
    return <Skeleton variant="rounded" height={160} role="status" sx={{ mt: 4 }} />;
  }

  const isDefault = current.source === 'DEFAULT';
  const initial = isDefault ? (browser ?? 'UTC') : current.timezone;
  const selected = picked ?? initial;
  const unchanged = !isDefault && selected === current.timezone;
  const saveDisabled = saving || selected === '' || unchanged;
  const suggested = isDefault && picked === null && browser !== null;
  const from = isDefault ? t('config.timezone.utcNotSet', 'UTC (not set)') : current.timezone;

  function choose(zone: string) {
    setPicked(zone);
    setFieldError(null);
    setSaveFailure(null);
  }

  async function onConfirm() {
    if (savingRef.current) return;
    savingRef.current = true;
    setConfirmOpen(false);
    setSaving(true);
    setFieldError(null);
    setSaveFailure(null);
    try {
      const result = await putTimezone(selected);
      const timezone = result.timezone !== '' ? result.timezone : selected;
      setSaved({
        tenantSlug: result.tenantSlug !== '' ? result.tenantSlug : (current?.tenantSlug ?? ''),
        timezone,
        source: 'SETTINGS',
      });
      setPicked(timezone);
      if (!result.changed) {
        setNotice(t('config.timezone.noChange', 'No change.'));
      } else if (result.build !== null) {
        setNotice(t('config.timezone.savedRebuilding', 'Saved — reports are rebuilding.'));
      } else if (result.rebuildMarked) {
        setNotice(t('config.timezone.savedQueued', 'Saved. Reports will rebuild on the next run.'));
      } else {
        setNotice(t('config.timezone.saved', 'Saved.'));
      }
    } catch (err) {
      if (isInvalidTimezone(err)) {
        const message = err instanceof ApiError ? err.details?.['message'] : undefined;
        setFieldError(
          typeof message === 'string' && message.length > 0
            ? message
            : t('config.timezone.invalid', 'That isn’t a recognised timezone.'),
        );
      } else {
        setSaveFailure(err);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      setRefocus((n) => n + 1);
    }
  }

  const label = t('config.timezone.label', 'Timezone');
  const helperText =
    fieldError ??
    (suggested
      ? t('config.timezone.suggested', 'Suggested from this browser — not saved yet.')
      : undefined);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 4 }} component="section">
      <Typography variant="h2" component="h2" sx={{ mb: 1 }}>
        {t('config.timezone.title', 'Studio timezone')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'config.timezone.help',
          'Decides which day a sale, a check-in and a report bucket belong to.',
        )}
      </Typography>

      {isDefault ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            'config.timezone.notSet',
            'Not set — using UTC. Choose your studio’s timezone so evening sales and check-ins land on the right day.',
          )}
        </Alert>
      ) : null}

      {saveFailure !== null ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('config.timezone.saveError', 'Could not save the studio timezone.')}
          <ErrorDetails
            error={saveFailure}
            headline={t('config.timezone.saveError', 'Could not save the studio timezone.')}
          />
        </Alert>
      ) : null}

      <Box sx={{ width: '100%' }}>
        {options === null ? (
          <TextField
            fullWidth
            label={label}
            value={selected}
            onChange={(event) => choose(event.target.value)}
            error={fieldError !== null}
            helperText={helperText}
            inputRef={pickerRef}
          />
        ) : (
          <Autocomplete
            fullWidth
            disableClearable
            options={options}
            value={selected}
            onChange={(_event, zone) => choose(zone)}
            getOptionLabel={zoneLabel}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={fieldError !== null}
                helperText={helperText}
                inputRef={pickerRef}
              />
            )}
          />
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          disabled={saveDisabled}
          onClick={() => setConfirmOpen(true)}
          ref={saveRef}
        >
          {t('config.timezone.save', 'Save timezone')}
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t('config.timezone.confirmTitle', 'Change the studio timezone?')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t(
              'config.timezone.confirmBody',
              'Changing the timezone rebuilds every report for this studio. Totals per day and per month may shift where sales or check-ins happened near midnight. This takes a few minutes.',
            )}
          </Typography>
          <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {`${from} → ${selected}`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t('config.timezone.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => void onConfirm()}>
            {t('config.timezone.confirm', 'Change timezone')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={notice !== null} autoHideDuration={6000} onClose={() => setNotice(null)}>
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
